import React, { lazy, Suspense } from 'react';
import { Element } from 'react-scroll';
import { EmailSettings } from './../AkazioConfig';
import { withRouter, RouteComponentProps, Link } from 'react-router-dom';
import { LinkPaths } from './Constants';
import { DemoFormPurpose } from './BookDemoForm';
import { OpenCookiePreferences } from '../analytics/ConsentManager';
import { GeoLocEnum, LegalLinkPaths } from './legal/Links';
import styled from 'styled-components';
import { MQ } from '../style/MediaQueryHelpers';

const BookDemoForm = lazy(() => import('./BookDemoForm'));
const SuspendedRoute = (a: JSX.Element) => <Suspense fallback={<div>Loading...</div>}>{a}</Suspense>;

type AkazioFooterProps = {};

export const doNotShowBookDemoElementPages = [LinkPaths.CONTACT_SALES, LinkPaths.CONTACT_SALES_THANKYOU];

const FooterContainer = styled.div`
	display: grid;
	grid-template-columns: 1fr 200px 200px 200px;
	grid-gap: 0px 40px;

	max-width: 1200px;
	margin-left: auto;
	margin-right: auto;

	h1,
	h2,
	h3 {
		font-family: 'Montserrat', sans-serif;
	}

	${MQ.W980} {
		grid-template-columns: 100%;
		margin-left: 0;
		margin-right: 0;
		grid-gap: 30px 0px;
	}
`;

const FlexRow = styled.div`
	display: flex;
	flex-direction: row;
	text-align: left;
`;

const VerticalDivider = styled.div`
	height: 50px;
	background: white;
	width: 2px;
	margin: auto 10px;
`;

const FooterHeader = styled.h2`
	text-transform: uppercase;
`;

const FooterHeaderNonTop = styled(FooterHeader)`
	margin-top: 10px;
`;

const MissionStatement = styled(FooterHeader)`
	max-width: 300px;
	margin: auto 0;
	color: var(--footer-color) !important;
	font-size: 13px;
`;

const FooterLink = styled.p`
	text-transform: uppercase;
	color: var(--footer-color);
	margin: 0px;
	margin-right: 5px;
	margin-left: 5px;
	margin-bottom: 5px;
	font-size: 12px;
	cursor: pointer;

	:hover {
		margin-left: 10px;
		margin-right: 0px;
		color: var(--footer-link-hover);
	}

	${MQ.W640} {
		// for mbile make sure they are clickable enough
		margin-top: 5px;
		margin-bottom: 15px;
	}
`;

const ConnectLinkWrapper = styled.div`
	a {
		font-size: 14px;
	}

	margin-top: 10px;
`;

const AkazioFooter: React.FC<AkazioFooterProps & RouteComponentProps> = (props) => {
	const showBookDemoElement = !doNotShowBookDemoElementPages.includes(props.location.pathname);

	return (
		<FooterContainer>
			<Link to={LinkPaths.HOME}>
				<FlexRow>
					<img src="/favicon.svg" width={50} alt="akazio" />
					<VerticalDivider />
					<MissionStatement>Cloud archiving for the connected workplace</MissionStatement>
				</FlexRow>
			</Link>
			<div>
				<FooterHeader>Connect with us</FooterHeader>
				<ConnectLinkWrapper>
					<a href={`mailto:${EmailSettings.HiEmail}`}>{EmailSettings.HiEmail}</a>
				</ConnectLinkWrapper>
				<ConnectLinkWrapper>
					<a href="tel:+17185048373">+1 (718) 504-8373</a>
				</ConnectLinkWrapper>
				<ConnectLinkWrapper>
					<a href="tel:+442071933449">+44 20 7193 3449</a>
				</ConnectLinkWrapper>
			</div>
			<div>
				<FooterHeader>Contact</FooterHeader>
				<Link to={LinkPaths.CONTACT_SALES}>
					<FooterLink>Sales</FooterLink>
				</Link>
				<Link to={LinkPaths.CONTACT_SUPPORT}>
					<FooterLink>Support</FooterLink>
				</Link>
				<Link to={LinkPaths.CAREERS}>
					<FooterLink>Careers</FooterLink>
				</Link>
				<FooterHeaderNonTop>Secure Archive</FooterHeaderNonTop>
				<Link to={LinkPaths.SECURITY}>
					<FooterLink>Security</FooterLink>
				</Link>
				<Link to={LinkPaths.DATA_RESIDENCY}>
					<FooterLink>Data Residency</FooterLink>
				</Link>
				<FooterHeaderNonTop>Resources</FooterHeaderNonTop>
				<Link to={LinkPaths.CONNECTORS_PAGE}>
					<FooterLink>Connectors</FooterLink>
				</Link>
				<Link to={LinkPaths.PRICING}>
					<FooterLink>Pricing</FooterLink>
				</Link>
				<Link to={LinkPaths.EMAIL_ARCHIVING}>
					<FooterLink>Email Archiving</FooterLink>
				</Link>
				<Link to={LinkPaths.IM_ARCHIVING}>
					<FooterLink>Instant Message Archiving</FooterLink>
				</Link>
				<Link to={LinkPaths.AV_ARCHIVING}>
					<FooterLink>Audio &amp; Video Archiving</FooterLink>
				</Link>
				<Link to={LegalLinkPaths.TERMS(GeoLocEnum.us)}>
					<FooterLink>Legal</FooterLink>
				</Link>
				<Link to={LegalLinkPaths.TERMS_PRIVACY_POLICY(GeoLocEnum.us)}>
					<FooterLink>Privacy Policy</FooterLink>
				</Link>
				<FooterLink onClick={OpenCookiePreferences}>Cookie Preferences</FooterLink>
			</div>
			{showBookDemoElement && (
				<Element name="scroll-to-get-a-demo">
					<div>
						<FooterHeader>Request a Demo</FooterHeader>
						{SuspendedRoute(
							<BookDemoForm
								onCompleteCallback={() => props.history.push(LinkPaths.CONTACT_SALES_THANKYOU)}
								purpose={DemoFormPurpose.Sales}
							/>
						)}
					</div>
				</Element>
			)}
		</FooterContainer>
	);
};

export default withRouter(AkazioFooter);
